import {
  AddressLookupResponse,
  VoterFilePhone,
  VoterProfileType,
  VoterSearchPayload,
} from 'src/@types/voterSearch';
import { useGet } from './templateHooks';
import { FAST_API_URL } from 'src/config';
import { useAxios } from './useFetch';
import { useQuery } from '@tanstack/react-query';

export const useVoterSearch = (params: VoterSearchPayload, config = {}) => {
  const fetchData = useAxios<VoterProfileType[], undefined, VoterSearchPayload>({
    endpoint: '/v2/voter-search',
    method: 'get',
    url: FAST_API_URL,
  });

  return useQuery<VoterProfileType[]>({
    queryKey: ['voterSearch', params],
    queryFn: () => {
      const hasParams =
        Object.values(params).filter((value) => value !== undefined && value !== null).length > 0;

      if (!hasParams) {
        throw new Error('At least one parameter must be provided for voter search.');
      }
      return fetchData({ params: params });
    },
    ...config,
  });
};

export const useVoterSearchAddressLookup = (params: VoterSearchPayload, config = {}) => {
  const fetchData = useAxios<AddressLookupResponse, undefined, VoterSearchPayload>({
    endpoint: '/v1/voter-search/address',
    method: 'get',
    url: FAST_API_URL,
  });

  return useQuery<AddressLookupResponse>({
    queryKey: ['voterSearchAddressLookup', params],
    queryFn: () => {
      const hasParams =
        Object.values(params).filter((value) => value !== undefined && value !== null).length > 0;

      if (!hasParams) {
        throw new Error('At least one parameter must be provided for voter search.');
      }
      return fetchData({ params: params });
    },
    ...config,
  });
};

export const useVoter = (voterId: string, config = {}) =>
  useGet<VoterProfileType>({
    queryKey: ['voter', voterId],
    endpoint: `/v2/voter-lookup/${voterId}`,
    config: config,
    url: FAST_API_URL,
  });

export const useVoterSearchCounties = (state: string, config = {}) =>
  useGet<string[]>({
    queryKey: ['counties', state],
    endpoint: `/v1/voter-search/${state}/counties`,
    config,
    url: FAST_API_URL,
  });

export const useVoterSearchPrecincts = (state: string, county: string, config = {}) =>
  useGet<string[], { county_name: string }>({
    queryKey: ['precincts', state, county],
    endpoint: `/v1/voter-search/${state}/precincts`,
    config,
    params: {
      county_name: county,
    },
    url: FAST_API_URL,
  });

export const useVoterFilePhones = (numinarId: string, config = {}) =>
  useGet<VoterFilePhone[]>({
    queryKey: ['voterFilePhones', numinarId],
    endpoint: `/v1/voters/${numinarId}/phones`,
    config,
    url: FAST_API_URL,
  });

export const useProjectVoter = (projectId: string, voterId: string, config = {}) =>
  useGet<VoterProfileType>({
    queryKey: ['projectvoter', projectId, voterId],
    endpoint: `/v3/projects/${projectId}/voters`,
    resourceId: voterId,
    url: FAST_API_URL,
    config: config,
  });
